import * as XLSX from 'xlsx';
import { studentName } from './hubUtils';

const aw = (cols) => cols.map((w) => ({ wch: w }));

const rawMark = (a) => {
  if (!a || a.is_absent || a.is_exempted) return null;
  const v = parseFloat(a.score ?? a.marks_obtained ?? a.raw_score ?? 0);
  return isNaN(v) ? null : v;
};

const markStr = (a) => {
  if (!a) return '-';
  if (a.is_absent) return 'ABS';
  if (a.is_exempted) return 'EXM';
  const v = rawMark(a);
  return v != null ? v.toFixed(0) : '-';
};

const meta = (classLabel, termLabel, extra = []) => [
  ['School Sync — Export'],
  ['Scope:', classLabel],
  ['Term:', termLabel],
  ['Exported:', new Date().toLocaleString()],
  ...extra,
  [],
];

// ── Report 1: Class Mark List ─────────────────────────────────────────────────
// Students × Subjects × Assessments — raw marks, aggregate, division, position
const buildMarksSheet = (rows, subjects, assessmentCols, classLabel, termLabel) => {
  const header = [
    '#', 'Student', 'Reg No', 'Gender',
    ...subjects.flatMap((s) => assessmentCols.map((col) => `${s.code || s.name} ${col.code}`)),
    'Total', 'Aggregate', 'Division', 'Position',
  ];

  const dataRows = rows.map((row, idx) => {
    const r = [
      idx + 1,
      studentName(row.student),
      row.student?.registration_no || '-',
      row.student?.gender ? row.student.gender.charAt(0).toUpperCase() : '-',
    ];
    subjects.forEach((subj) => {
      assessmentCols.forEach((col) => {
        const a = (row.marksBySubject[subj.id] || {})[col.code];
        const v = rawMark(a);
        r.push(v != null ? v : markStr(a));
      });
    });
    r.push(
      row.total.toFixed(0),
      row.aggregate ?? '-',
      row.division ?? '-',
      row.class_position != null
        ? `${row.class_position}${row.class_total_students ? `/${row.class_total_students}` : ''}`
        : '-',
    );
    return r;
  });

  // Totals footer
  const totalsRow = ['', 'TOTALS', '', ''];
  subjects.forEach((subj) => {
    assessmentCols.forEach((col) => {
      const t = rows.reduce((sum, row) => {
        const v = rawMark((row.marksBySubject[subj.id] || {})[col.code]);
        return sum + (v ?? 0);
      }, 0);
      totalsRow.push(t.toFixed(0));
    });
  });
  totalsRow.push(rows.reduce((s, r) => s + r.total, 0).toFixed(0), '', '', '');

  const ws = XLSX.utils.aoa_to_sheet([
    ...meta(classLabel, termLabel, [['Students:', rows.length]]),
    header,
    ...dataRows,
    totalsRow,
  ]);
  ws['!cols'] = aw([4, 24, 14, 6, ...subjects.flatMap(() => assessmentCols.map(() => 10)), 8, 10, 14, 10]);
  return ws;
};

// ── Report 2: Performance Report ──────────────────────────────────────────────
// Top / Middle / Bottom bands
const buildPerformanceSheet = (rows) => {
  const top    = rows.filter((r) => r.overall_percentage >= 80);
  const middle = rows.filter((r) => r.overall_percentage >= 50 && r.overall_percentage < 80);
  const bottom = rows.filter((r) => r.overall_percentage < 50);
  const pct    = (n) => rows.length ? `${((n / rows.length) * 100).toFixed(1)}%` : '-';

  const summary = [
    ['Performance Band', 'Count', '% of Group'],
    ['Top (≥ 80%)',      top.length,    pct(top.length)],
    ['Middle (50–79%)',  middle.length, pct(middle.length)],
    ['Bottom (< 50%)',   bottom.length, pct(bottom.length)],
    [],
  ];

  const detail = (label, bandRows) => [
    [label],
    ['#', 'Student', 'Reg No', 'Gender', 'Total', 'Aggregate', 'Division', 'Position'],
    ...bandRows.map((row, idx) => [
      idx + 1,
      studentName(row.student),
      row.student?.registration_no || '-',
      row.student?.gender ? row.student.gender.charAt(0).toUpperCase() : '-',
      row.total.toFixed(0),
      row.aggregate ?? '-',
      row.division ?? '-',
      row.class_position != null ? `${row.class_position}${row.class_total_students ? `/${row.class_total_students}` : ''}` : '-',
    ]),
    [],
  ];

  const ws = XLSX.utils.aoa_to_sheet([
    ...summary,
    ...detail('TOP PERFORMERS (≥ 80%)', top),
    ...detail('MIDDLE PERFORMERS (50–79%)', middle),
    ...detail('BOTTOM PERFORMERS (< 50%)', bottom),
  ]);
  ws['!cols'] = aw([4, 24, 14, 7, 8, 10, 14, 10]);
  return ws;
};

// ── Report 3: Subject Averages ────────────────────────────────────────────────
const buildSubjectAvgSheet = (rows, subjects, assessmentCols) => {
  const header = ['Subject', ...assessmentCols.map((c) => `${c.code} Avg`), ...assessmentCols.map((c) => `${c.code} High`), ...assessmentCols.map((c) => `${c.code} Low`), ...assessmentCols.map((c) => `${c.code} Pass%`)];

  const dataRows = subjects.map((subj) => {
    const r = [subj.name];
    assessmentCols.forEach((col) => {
      const vals = rows.map((row) => rawMark((row.marksBySubject[subj.id] || {})[col.code])).filter((v) => v != null);
      r.push(vals.length ? (vals.reduce((s, v) => s + v, 0) / vals.length).toFixed(1) : '-');
    });
    assessmentCols.forEach((col) => {
      const vals = rows.map((row) => rawMark((row.marksBySubject[subj.id] || {})[col.code])).filter((v) => v != null);
      r.push(vals.length ? Math.max(...vals).toFixed(0) : '-');
    });
    assessmentCols.forEach((col) => {
      const vals = rows.map((row) => rawMark((row.marksBySubject[subj.id] || {})[col.code])).filter((v) => v != null);
      r.push(vals.length ? Math.min(...vals).toFixed(0) : '-');
    });
    assessmentCols.forEach((col) => {
      const cfg = assessmentCols.find((c) => c.code === col.code);
      const maxM = parseFloat(cfg?.max_marks || 0);
      const vals = rows.map((row) => rawMark((row.marksBySubject[subj.id] || {})[col.code])).filter((v) => v != null);
      const pass = vals.filter((v) => maxM > 0 ? v >= maxM * 0.5 : v >= 50).length;
      r.push(vals.length ? `${((pass / vals.length) * 100).toFixed(1)}%` : '-');
    });
    return r;
  });

  const ws = XLSX.utils.aoa_to_sheet([header, ...dataRows]);
  ws['!cols'] = aw([28, ...assessmentCols.flatMap(() => [10, 10, 10, 10])]);
  return ws;
};

// ── Report 4: Per-Assessment Report ──────────────────────────────────────────
// One sheet per assessment type
const buildAssessmentSheets = (rows, subjects, assessmentCols) => {
  return assessmentCols.map((col) => {
    const header = ['#', 'Student', 'Reg No', 'Gender', ...subjects.map((s) => s.code || s.name), 'Total', 'Aggregate', 'Division', 'Position'];
    const dataRows = rows.map((row, idx) => {
      const r = [
        idx + 1,
        studentName(row.student),
        row.student?.registration_no || '-',
        row.student?.gender ? row.student.gender.charAt(0).toUpperCase() : '-',
      ];
      let total = 0;
      subjects.forEach((subj) => {
        const a = (row.marksBySubject[subj.id] || {})[col.code];
        const v = rawMark(a);
        r.push(v != null ? v : markStr(a));
        if (v != null) total += v;
      });
      const colAggregate = row.colAggregates?.[col.code]?.aggTotal;
      const colDivision = row.colAggregates?.[col.code]?.division;
      const colPosition = row.colPositions?.[col.code];
      const overallPosition = row.class_position != null
        ? `${row.class_position}${row.class_total_students ? `/${row.class_total_students}` : ''}`
        : '-';

      r.push(
        total.toFixed(0),
        colAggregate ?? (assessmentCols.length === 1 ? row.aggregate ?? '-' : '-'),
        colDivision ?? (assessmentCols.length === 1 ? row.division ?? '-' : '-'),
        colPosition?.rank != null
          ? `${colPosition.rank}${colPosition.total ? `/${colPosition.total}` : ''}`
          : (assessmentCols.length === 1 ? overallPosition : '-')
      );
      return r;
    });

    // Totals row
    const totalsRow = ['', 'TOTALS', '', ''];
    subjects.forEach((subj) => {
      const t = rows.reduce((sum, row) => {
        const v = rawMark((row.marksBySubject[subj.id] || {})[col.code]);
        return sum + (v ?? 0);
      }, 0);
      totalsRow.push(t.toFixed(0));
    });
    totalsRow.push(
      rows.reduce((sum, row) => {
        const subjectTotal = subjects.reduce((acc, subj) => {
          const v = rawMark((row.marksBySubject[subj.id] || {})[col.code]);
          return acc + (v ?? 0);
        }, 0);
        return sum + subjectTotal;
      }, 0).toFixed(0),
      '',
      '',
      ''
    );

    const ws = XLSX.utils.aoa_to_sheet([header, ...dataRows, totalsRow]);
    ws['!cols'] = aw([4, 24, 14, 6, ...subjects.map(() => 10), 8, 10, 14, 10]);
    return { name: col.code, ws };
  });
};

// ── Report 5: Student Summary ─────────────────────────────────────────────────
// One row per student — all assessments combined, aggregate, division, position
const buildStudentSummarySheet = (rows, subjects, assessmentCols, classLabel, termLabel) => {
  const header = [
    '#', 'Student', 'Reg No', 'Gender',
    ...subjects.map((s) => s.code || s.name),
    'Total', 'Aggregate', 'Division', 'Position',
  ];

  const dataRows = rows.map((row, idx) => {
    const r = [
      idx + 1,
      studentName(row.student),
      row.student?.registration_no || '-',
      row.student?.gender ? row.student.gender.charAt(0).toUpperCase() : '-',
    ];
    subjects.forEach((subj) => {
      // Sum all assessment marks for this subject
      let subjTotal = 0;
      let hasAny = false;
      assessmentCols.forEach((col) => {
        const v = rawMark((row.marksBySubject[subj.id] || {})[col.code]);
        if (v != null) { subjTotal += v; hasAny = true; }
      });
      r.push(hasAny ? subjTotal.toFixed(0) : '-');
    });
    r.push(
      row.total.toFixed(0),
      row.aggregate ?? '-',
      row.division ?? '-',
      row.class_position != null
        ? `${row.class_position}${row.class_total_students ? `/${row.class_total_students}` : ''}`
        : '-',
    );
    return r;
  });

  const ws = XLSX.utils.aoa_to_sheet([
    ...meta(classLabel, termLabel, [['Students:', rows.length]]),
    header,
    ...dataRows,
  ]);
  ws['!cols'] = aw([4, 24, 14, 6, ...subjects.map(() => 10), 8, 10, 14, 10]);
  return ws;
};

// ── Public entry point ────────────────────────────────────────────────────────
export const exportReport = (type, rows, subjects, assessmentCols, classLabel, filters) => {
  try {
    if (!rows?.length) throw new Error('No data to export');
    const termLabel = filters?.academic_year || '';
    const slug = (classLabel || 'export').replace(/[^a-z0-9]/gi, '_');
    const date = new Date().toISOString().split('T')[0];
    const wb = XLSX.utils.book_new();

    if (type === 'marks') {
      XLSX.utils.book_append_sheet(wb, buildMarksSheet(rows, subjects, assessmentCols, classLabel, termLabel), 'Marks');
      XLSX.writeFile(wb, `class-marks-${slug}-${date}.xlsx`);

    } else if (type === 'performance') {
      XLSX.utils.book_append_sheet(wb, buildPerformanceSheet(rows), 'Performance');
      XLSX.writeFile(wb, `performance-${slug}-${date}.xlsx`);

    } else if (type === 'subject_avg') {
      XLSX.utils.book_append_sheet(wb, buildSubjectAvgSheet(rows, subjects, assessmentCols), 'Subject Averages');
      XLSX.writeFile(wb, `subject-averages-${slug}-${date}.xlsx`);

    } else if (type === 'assessment') {
      const sheets = buildAssessmentSheets(rows, subjects, assessmentCols);
      sheets.forEach(({ name, ws }) => XLSX.utils.book_append_sheet(wb, ws, name));
      XLSX.writeFile(wb, `per-assessment-${slug}-${date}.xlsx`);

    } else if (type === 'student') {
      XLSX.utils.book_append_sheet(wb, buildStudentSummarySheet(rows, subjects, assessmentCols, classLabel, termLabel), 'Student Summary');
      XLSX.writeFile(wb, `student-summary-${slug}-${date}.xlsx`);
    }

    return { success: true, message: `Exported ${rows.length} students` };
  } catch (err) {
    return { success: false, message: err.message };
  }
};

// Keep old exports for backward compat
export const exportMarksToExcel = (rows, subjects, assessmentCols, filters, classLabel) =>
  exportReport('marks', rows, subjects, assessmentCols, classLabel, filters);

export const exportMarksToCsv = (rows, subjects, assessmentCols, filters, classLabel) => {
  try {
    if (!rows?.length) throw new Error('No data to export');
    const header = ['Student', 'Reg No', ...subjects.flatMap((s) => assessmentCols.map((col) => `${s.code || s.name} ${col.code}`)), 'Total', 'Aggregate', 'Division', 'Position'];
    const dataRows = rows.map((row) => {
      const r = [studentName(row.student), row.student?.registration_no || '-'];
      subjects.forEach((subj) => assessmentCols.forEach((col) => {
        const v = rawMark((row.marksBySubject[subj.id] || {})[col.code]);
        r.push(v != null ? v.toFixed(0) : markStr((row.marksBySubject[subj.id] || {})[col.code]));
      }));
      r.push(row.total.toFixed(0), row.aggregate ?? '-', row.division ?? '-',
        row.class_position != null ? `${row.class_position}${row.class_total_students ? `/${row.class_total_students}` : ''}` : '-');
      return r;
    });
    const csv = [header, ...dataRows].map((r) => r.map((v) => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `marks-${(classLabel || 'export').replace(/[^a-z0-9]/gi, '_')}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    return { success: true, message: 'CSV exported' };
  } catch (err) {
    return { success: false, message: err.message };
  }
};
